<template>
	<div class="request-list-page">
		<header class="request-list-toolbar">
			<button type="button" class="new-request-action" @click="newTicket()">
				<portal-icon name="plus" :size="17"></portal-icon>
				{{ $t('requests.new-request') }}
			</button>
		</header>

		<div class="request-list-content show-scrollbar">
			<div class="request-table-shell portal-compact-list-shell">
				<v-data-table
					v-if="isReady"
					class="request-table portal-compact-table"
					:headers="headers"
					sort-by="updated"
					:sort-desc="true"
					:must-sort="true"
					:items="filteredTickets"
					:disable-pagination="true"
					:hide-default-footer="true"
					style="cursor: pointer"
					:mobile-breakpoint="200"
					:no-data-text="$t('requests.no-found-requests')"
					@click:row="viewTicketDetail($event)"
				>
					<template v-slot:item.status="{ item }">
						<div class="row-format align-center">
							<div v-if="item.unreadClient" class="umi mr-2"></div>
							<portal-status-pill
								v-if="item.open"
								:background="ticketStatusBackground(item, ticketTypes)"
								color="var(--portal-text)"
							>
								{{ item.status }}
							</portal-status-pill>
							<portal-status-pill
								v-else
								background="var(--v-alert_10-base)"
								color="var(--v-alert-base)"
							>
								{{ $t('requests.closed') }}
							</portal-status-pill>
						</div>
					</template>

					<template v-slot:item.type="{ item }">
						<span
							v-if="getTicketType(item.type)"
							class="portal-cell-truncate"
							:title="getTicketType(item.type).type"
						>
							{{ getTicketType(item.type).type }}
						</span>
						<span v-else class="portal-cell-muted">—</span>
					</template>

					<template v-slot:item.updated="{ item }">
						<span
							class="portal-cell-truncate portal-cell-muted"
							:title="DateTime.fromISO(item.updated).toLocaleString(DateTime.DATETIME_MED)"
						>
							{{ DateTime.fromISO(item.updated).toFormat('MMM d, yyyy') }}
						</span>
					</template>

					<template v-slot:item.subject="{ item }">
						<span class="portal-cell-truncate portal-cell-primary" :title="item.subject">
							{{ item.subject }}
						</span>
					</template>

					<template v-slot:item.lastComment="{ item }">
						<div
							class="portal-cell-truncate portal-cell-muted"
							:title="plainText(item.lastComment)"
							v-html="item.lastComment"
						></div>
					</template>

					<template v-slot:no-data>
						<div class="request-empty">
							<span class="request-empty-icon">
								<portal-icon name="ticket" :size="21"></portal-icon>
							</span>
							<span>{{ $t('requests.no-found-requests') }}</span>
						</div>
					</template>
				</v-data-table>
				<div v-else class="request-list-loading">
					<v-progress-circular indeterminate :size="34" :width="3" color="primary"></v-progress-circular>
				</div>
			</div>

			<button
				v-if="isReady && closedTicketCount"
				type="button"
				class="closed-request-toggle"
				:aria-expanded="showClosedTickets ? 'true' : 'false'"
				@click="toggleClosedTickets"
			>
				<span>
					{{ showClosedTickets ? $t('requests.list.hide') : $t('requests.list.show') }}
					{{ closedTicketCount }} {{ $t('requests.list.closed') }}
				</span>
				<portal-icon :name="showClosedTickets ? 'chevron-up' : 'chevron-down'" :size="16"></portal-icon>
			</button>
		</div>
	</div>
</template>

<script>
	import TicketService from '@/modules/tickets/TicketService';
	import { ticketStatusBackground } from '@/modules/tickets/ticketStatus';
	import { DateTime } from 'luxon';

	export default {
		name: 'TicketList',

		props: [],

		components: {},

		data: function() {
			return {
				DateTime: DateTime,
				ticketService: new TicketService(),
				ticketTypes: [],
				tickets: [],
				showClosedTickets: false,
				isReady: false,
			};
		},

		mounted() {
			this.initialize();
			this.setupTicketListeners();
		},

		beforeDestroy() {
			this.stopTicketListeners();
		},

		methods: {
			ticketStatusBackground,
			setupTicketListeners: function() {
				this.$store.state.eventBus.$on(
					this.$store.getters.getMessageChannelPrefix + '.ticketCreated',
					this.ticketNotification
				);
				this.$store.state.eventBus.$on(
					this.$store.getters.getMessageChannelPrefix + '.ticketUpdated',
					this.ticketNotification
				);
				this.$store.state.eventBus.$on(
					this.$store.getters.getMessageChannelPrefix + '.ticketComment',
					this.ticketNotification
				);
				this.$store.state.eventBus.$on(
					this.$store.getters.getMessageChannelPrefix + '.ticketDeleted',
					this.ticketNotification
				);
			},

			stopTicketListeners: function() {
				this.$store.state.eventBus.$off(
					this.$store.getters.getMessageChannelPrefix + '.ticketCreated',
					this.ticketNotification
				);
				this.$store.state.eventBus.$off(
					this.$store.getters.getMessageChannelPrefix + '.ticketUpdated',
					this.ticketNotification
				);
				this.$store.state.eventBus.$off(
					this.$store.getters.getMessageChannelPrefix + '.ticketComment',
					this.ticketNotification
				);
				this.$store.state.eventBus.$off(
					this.$store.getters.getMessageChannelPrefix + '.ticketDeleted',
					this.ticketNotification
				);
			},

			toggleClosedTickets: function() {
				this.showClosedTickets = !this.showClosedTickets;
			},

			ticketNotification: function(event) {
				if (event.channel.includes('ticketUpdated')) {
					let ticket = event.message;
					let ix = this.tickets.findIndex((t) => t.id === ticket.id);
					if (ix > -1) {
						this.tickets.splice(ix, 1, ticket);
					}
				} else if (event.channel.includes('ticketComment')) {
					let ticket = event.message.ticket;
					let ix = this.tickets.findIndex((t) => t.id === ticket.id);
					if (ix > -1) {
						this.tickets.splice(ix, 1, ticket);
					}
				} else if (event.channel.includes('ticketCreated')) {
					this.tickets.push(event.message.ticket);
				} else if (event.channel.includes('ticketDeleted')) {
					let ticketId = event.message;
					let ix = this.tickets.findIndex((t) => t.id === ticketId);
					if (ix > -1) {
						this.tickets.splice(ix, 1);
					}
				}
			},

			initialize: function() {
				this.getTickets();
				this.getTicketTypes();
			},

			viewTicketDetail: function(ticket) {
				this.$router.push(`/requests/${ticket.id}`);
			},

			getTicketTypes: function() {
				this.ticketService.getTicketTypes().then((res) => {
					this.ticketTypes.splice(0, this.ticketTypes.length);
					this.ticketTypes.push(...res.data);
				});
			},

			getTickets: function() {
				this.ticketService
					.getTicketList()
					.then((res) => {
						this.tickets.splice(0, this.tickets.length);
						this.tickets.push(...res.data);
					})
					.finally(() => {
						this.isReady = true;
					});
			},

			plainText: function(value) {
				return (value || '')
					.replace(/<[^>]*>/g, ' ')
					.replace(/\s+/g, ' ')
					.trim();
			},

			getTicketType: function(id) {
				if (this.ticketTypes) {
					return this.ticketTypes.find((t) => t.id === id);
				} else {
					return {};
				}
			},

			newTicket: function() {
				this.$router.push('/requests/new');
			},
		},

		computed: {
			headers: function() {
				return [
					{ text: '#', value: 'ticketNumber', width: 64 },
					{ text: this.$t('requests.list.status'), value: 'status', width: 100 },
					{
						text: this.$t('requests.list.type'),
						value: 'type',
						width: 170,
						class: 'request-type-column portal-list-priority-medium',
						cellClass: 'request-type-column portal-list-priority-medium',
					},
					{
						text: this.$t('requests.list.subject'),
						value: 'subject',
						class: 'portal-list-grow',
						cellClass: 'portal-list-grow',
					},
					{
						text: this.$t('requests.list.updated'),
						value: 'updated',
						width: 136,
						class: 'request-updated-column portal-list-priority-compact',
						cellClass: 'request-updated-column portal-list-priority-compact',
					},
					{
						text: this.$t('requests.list.last-comment'),
						value: 'lastComment',
						class: 'request-comment-column portal-list-priority-low',
						cellClass: 'request-comment-column portal-list-priority-low',
					},
				];
			},

			closedTicketCount: function() {
				return this.tickets.filter((t) => !t.open).length;
			},

			filteredTickets: function() {
				if (this.showClosedTickets) {
					return this.tickets;
				} else {
					return this.tickets.filter((t) => t.open);
				}
			},
		},
	};
</script>

<style scoped lang="scss">
	.request-list-page {
		min-height: 100%;
		display: flex;
		flex-direction: column;
	}

	.request-list-toolbar {
		display: flex;
		justify-content: flex-end;
		padding: 16px 16px 0;
	}

	.new-request-action {
		min-height: 40px;
		display: inline-flex;
		align-items: center;
		gap: 7px;
		padding: 0 14px;
		border: 0;
		border-radius: var(--brand-button-radius, 10px);
		background: var(--v-primary-base);
		color: var(--v-white-base);
		font: inherit;
		font-size: 13px;
		font-weight: 680;
		cursor: pointer;
		box-shadow: 0 5px 14px color-mix(in srgb, var(--v-primary-base) 20%, transparent);
	}

	.new-request-action:hover {
		background: color-mix(in srgb, var(--v-primary-base) 88%, black);
	}

	.new-request-action:focus-visible,
	.closed-request-toggle:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	.request-list-content {
		min-height: 0;
		flex: 1;
		padding: 14px 16px 24px;
		overflow-y: auto;
		text-align: left;
	}

	.request-table-shell {
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
		overflow: hidden;
	}

	.request-table ::v-deep .v-data-table__wrapper {
		border-radius: inherit;
	}

	.request-list-loading {
		min-height: 220px;
		display: grid;
		place-items: center;
	}

	.umi {
		width: 8px;
		height: 8px;
		flex: 0 0 8px;
		background-color: var(--v-primary-base);
		border-radius: 999px;
	}

	.request-empty {
		min-height: 180px;
		display: grid;
		place-content: center;
		justify-items: center;
		gap: 10px;
		color: var(--portal-muted);
		font-size: 13px;
	}

	.request-empty-icon {
		width: 42px;
		height: 42px;
		display: grid;
		place-items: center;
		border-radius: 13px;
		background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.closed-request-toggle {
		min-height: 36px;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-top: 12px;
		padding: 0 11px;
		border: 1px solid transparent;
		border-radius: var(--brand-button-radius, 9px);
		background: transparent;
		color: var(--portal-muted);
		font: inherit;
		font-size: 12px;
		font-weight: 650;
		cursor: pointer;
	}

	.closed-request-toggle:hover {
		border-color: var(--portal-border);
		background: var(--portal-surface);
		color: var(--portal-text);
	}

	@media (max-width: 599px) {
		.request-list-toolbar {
			padding: 12px 12px 0;
		}

		.new-request-action {
			width: 100%;
			justify-content: center;
		}

		.request-list-content {
			padding: 12px;
		}

		.request-table ::v-deep th,
		.request-table ::v-deep td {
			padding-right: 9px;
			padding-left: 9px;
		}
	}
</style>
